// src/features/execucao/popups/FechamentoModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./FechamentoModal.css";
import { getCausadores, getCausasPrimarias, getCausasSecundarias } from "../api";

const PORTAL_ID = "pai-portal-root";

function usePortalRoot(id = PORTAL_ID) {
  const elRef = useRef(null);
  if (!elRef.current) {
    const existing = document.getElementById(id);
    if (existing) elRef.current = existing;
    else {
      const el = document.createElement("div");
      el.setAttribute("id", id);
      document.body.appendChild(el);
      elRef.current = el;
    }
  }
  return elRef.current;
}

/* Helper número pt-BR */
const parsePtBRNumber = (text) => {
  if (text == null || text === "") return null;
  const normalized = String(text).replace(/\s+/g, "").replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

export default function FechamentoModal({
  open = true,
  initial = {},          // dados atuais da ficha (para validar faltantes)
  onClose = () => {},
  onConfirm = () => {},
}) {
  if (!open) return null;
  const portalRoot = usePortalRoot();

  // Campos do fechamento (sem autopreencher)
  const [responsavel, setResponsavel] = useState("");
  const [impacto, setImpacto] = useState("");
  const [causa, setCausa] = useState("");
  const [justificativa, setJustificativa] = useState("");

  // Opções dinâmicas
  const [causadores, setCausadores] = useState([]);
  const [primarias, setPrimarias] = useState([]);
  const [secundarias, setSecundarias] = useState([]);

  const [loadingCausadores, setLoadingCausadores] = useState(false);
  const [loadingPrimarias, setLoadingPrimarias] = useState(false);
  const [loadingSecundarias, setLoadingSecundarias] = useState(false);

  const [errCausadores, setErrCausadores] = useState("");
  const [errPrimarias, setErrPrimarias] = useState("");
  const [errSecundarias, setErrSecundarias] = useState("");

  // Popup de aviso
  const [showWarn, setShowWarn] = useState(false);
  const [missingList, setMissingList] = useState([]);
  const warnBtnRef = useRef(null);

  // Reset ao abrir
  useEffect(() => {
    if (!open) return;
    setResponsavel(""); setImpacto(""); setCausa(""); setJustificativa("");
    setShowWarn(false); setMissingList([]);
  }, [open]);

  // Foco no botão do aviso
  useEffect(() => {
    if (!showWarn) return;
    const t = setTimeout(() => warnBtnRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [showWarn]);

  // Carregar listas
  useEffect(() => {
    if (!open) return;
    setLoadingCausadores(true); setErrCausadores("");
    getCausadores()
      .then((arr) => setCausadores(Array.isArray(arr) ? arr : []))
      .catch(() => { setCausadores([]); setErrCausadores("Falha ao carregar Responsabilidades."); })
      .finally(() => setLoadingCausadores(false));
  }, [open]);

  useEffect(() => {
    setImpacto(""); setCausa("");
    setPrimarias([]); setSecundarias([]);
    setErrPrimarias(""); setErrSecundarias("");
    if (!responsavel) return;

    setLoadingPrimarias(true);
    getCausasPrimarias(responsavel)
      .then(arr => setPrimarias(Array.isArray(arr) ? arr : []))
      .catch(() => { setPrimarias([]); setErrPrimarias("Falha ao carregar Impactos."); })
      .finally(() => setLoadingPrimarias(false));
  }, [responsavel]);

  useEffect(() => {
    setCausa(""); setSecundarias([]); setErrSecundarias("");
    if (!responsavel || !impacto) return;

    setLoadingSecundarias(true);
    getCausasSecundarias(responsavel, impacto)
      .then(arr => setSecundarias(Array.isArray(arr) ? arr : []))
      .catch(() => { setSecundarias([]); setErrSecundarias("Falha ao carregar Causas."); })
      .finally(() => setLoadingSecundarias(false));
  }, [responsavel, impacto]);

  // Placeholders fixos
  const PH_RESP = "Selecione a responsabilidade";
  const PH_IMP  = "Selecione o impacto";
  const PH_CAU  = "Selecione a causa";

  const optsCausadores = useMemo(() => {
    if (loadingCausadores || errCausadores) return [];
    return causadores.map((c) => ({ value: c, label: c }));
  }, [loadingCausadores, errCausadores, causadores]);

  const optsPrimarias = useMemo(() => {
    if (!responsavel || loadingPrimarias || errPrimarias) return [];
    return primarias.map((p) => ({ value: p, label: p }));
  }, [responsavel, loadingPrimarias, errPrimarias, primarias]);

  const optsSecundarias = useMemo(() => {
    if (!responsavel || !impacto || loadingSecundarias || errSecundarias) return [];
    return secundarias.map((s) => ({ value: s, label: s }));
  }, [responsavel, impacto, loadingSecundarias, errSecundarias, secundarias]);

  // ===== Validação (apenas faltantes)
  const validateAll = () => {
    const missing = [];
    const isEmpty = (v) => String(v ?? "").trim() === "";

    // Da ficha (execução)
    const qtd = parsePtBRNumber(initial.quantidade_real ?? initial.qtd ?? "");
    if (!(Number(qtd) > 0)) missing.push("quantidade realizada");
    if (isEmpty(initial.primeira_previa)) missing.push("primeira Prévia");
    if (isEmpty(initial.segunda_previa))  missing.push("segunda prévia");
    if (isEmpty(initial.primeiro_inicio)) missing.push("inicio 1");
    if (isEmpty(initial.primeiro_fim))    missing.push("termino 1");

    // Do fechamento (este modal)
    if (!responsavel)           missing.push("Responsabilidade");
    if (!impacto)               missing.push("Impacto");
    if (!causa)                 missing.push("Causa");
    if (isEmpty(justificativa)) missing.push("Justificativa");

    return { ok: missing.length === 0, missing };
  };

  const tryConfirm = () => {
    const { ok, missing } = validateAll();
    if (!ok) {
      setMissingList(missing);
      setShowWarn(true);
      return;
    }
    onConfirm({
      responsavel: String(responsavel).trim(),
      impacto_fechamento: String(impacto).trim(),
      causa_fechamento: String(causa).trim(),
      justificativa_fechamento: String(justificativa || "").trim(),
    });
  };

  const modal = (
    <div className="finaliza__portalScope">
      {/* NÃO fecha clicando fora */}
      <div className="finaliza__backdrop" />
      <div
        className="finaliza__modal"
        role="dialog"
        aria-modal="true"
        aria-label="Fechamento da Execução"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="finaliza__header">
          <h3 className="finaliza__title">Fechamento</h3>
          <button className="finaliza__close" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="finaliza__body">
          <div className="finaliza__grid finaliza__grid--3">
            <div className="finaliza__item">
              <div className="finaliza__lbl">RESPONSABILIDADE <span className="req">*</span></div>
              <div className="finaliza__val">
                <select value={responsavel} onChange={(e) => setResponsavel(e.target.value)} aria-label="Responsabilidade">
                  <option value="">{PH_RESP}</option>
                  {optsCausadores.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div className="finaliza__item">
              <div className="finaliza__lbl">IMPACTO <span className="req">*</span></div>
              <div className="finaliza__val">
                <select value={impacto} onChange={(e) => setImpacto(e.target.value)} disabled={!responsavel} aria-label="Impacto">
                  <option value="">{PH_IMP}</option>
                  {optsPrimarias.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div className="finaliza__item">
              <div className="finaliza__lbl">CAUSA <span className="req">*</span></div>
              <div className="finaliza__val">
                <select value={causa} onChange={(e) => setCausa(e.target.value)} disabled={!responsavel || !impacto} aria-label="Causa">
                  <option value="">{PH_CAU}</option>
                  {optsSecundarias.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div className="finaliza__item is-span-3">
              <div className="finaliza__lbl">JUSTIFICATIVA <span className="req">*</span></div>
              <div className="finaliza__val">
                <textarea
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  rows={3}
                  placeholder="Explique resumidamente a justificativa do fechamento"
                  aria-label="Justificativa"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="finaliza__footer">
          <button className="finaliza__btn -ghost" onClick={onClose}>Cancelar</button>
          <button className="finaliza__btn" onClick={tryConfirm} title="Confirmar e Finalizar">
            Confirmar e Finalizar
          </button>
        </div>

        {showWarn && (
          <div className="finaliza__warnOverlay">
            <div
              className="finaliza__warnModal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="warn-title"
              aria-describedby="warn-desc"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="finaliza__warnIcon" aria-hidden="true">!</div>
              <h4 id="warn-title" className="finaliza__warnTitle">Faltam informações para finalizar</h4>
              <p id="warn-desc" className="finaliza__warnDesc">Preencha os itens abaixo:</p>

              {missingList.length > 0 && (
                <ul className="finaliza__warnList">
                  {missingList.map((m) => <li key={m}>{m}</li>)}
                </ul>
              )}

              <button
                ref={warnBtnRef}
                className="finaliza__btn -primary"
                onClick={() => setShowWarn(false)}
                autoFocus
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, portalRoot);
}
